import axios from 'axios';

const API_BASE_URL = '/api/migration';
const SERVERS_API_URL = '/api/servers';

const getAuthToken = () => localStorage.getItem('auth_token')

const addAuthInterceptor = (instance) => {
  instance.interceptors.request.use((config) => {
    const token = getAuthToken()
    if (token) config.headers['Authorization'] = `Bearer ${token}`
    return config
  })
}

/**
 * Append the JWT to an SSE/WebSocket URL as a query parameter.
 *
 * EventSource and WebSocket cannot set an Authorization header, so the token
 * travels in the query string instead; the backend's JwtAuthFilter accepts it
 * from either place. It stays inside the TLS tunnel, but note it will appear
 * in access logs, so these URLs should not be shared.
 */
const withToken = (url) => {
  const token = getAuthToken()
  if (!token) return url
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
}

/** Headers for raw fetch() calls, which bypass the axios interceptors. */
const authFetchHeaders = () => {
  const token = getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});
addAuthInterceptor(api);

const serversApi = axios.create({
  baseURL: SERVERS_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});
addAuthInterceptor(serversApi);

// Add request/response interceptors for debugging
serversApi.interceptors.request.use(
  (config) => {
    console.log('API REQUEST:', config.method?.toUpperCase(), config.url);
    console.log('API REQUEST DATA:', JSON.stringify(config.data, null, 2));
    return config;
  },
  (error) => {
    console.error('API REQUEST ERROR:', error);
    return Promise.reject(error);
  }
);

serversApi.interceptors.response.use(
  (response) => {
    console.log('API RESPONSE:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API RESPONSE ERROR:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);

// ========== MIGRATION API ==========

export const uploadAccountsFile = async (file, jobName = '') => {
  const formData = new FormData();
  formData.append('file', file);
  if (jobName) {
    formData.append('jobName', jobName);
  }
  
  const response = await api.post('/upload-accounts', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const inputAccountsManual = async (accounts, jobName = '') => {
  const response = await api.post('/input-accounts', {
    accounts,
    jobName,
  });
  return response.data;
};

// ========== DRAFT ACCOUNTS API ==========

export const saveDraftAccounts = async (accounts, jobName = '', jobId = null) => {
  const response = await api.post('/draft-accounts', {
    accounts,
    jobName,
    jobId,
  });
  return response.data;
};

export const getDraftAccounts = async (jobId) => {
  const response = await api.get(`/draft-accounts/${jobId}`);
  return response.data;
};

export const getAllDrafts = async () => {
  const response = await api.get('/drafts');
  return response.data;
};

// Step 1: Configure Servers
export const configureServers = async (jobId, sourceServerId, destinationServerId) => {
  const response = await api.post(`/jobs/${jobId}/configure-servers`, {
    sourceServerId,
    destinationServerId,
  });
  return response.data;
};

// Step 2: Test Connections
export const testConnections = async (jobId) => {
  const response = await api.post(`/jobs/${jobId}/test-connections`);
  return response.data;
};

// Step 3: Set Password Options
export const setPasswordOptions = async (jobId, updateSourcePasswords, storePasswords) => {
  const response = await api.post(`/jobs/${jobId}/password-options`, {
    updateSourcePasswords,
    storePasswords,
  });
  return response.data;
};

// Step 4: Create Destination Emails
export const createDestinationEmails = async (jobId) => {
  const response = await api.post(`/jobs/${jobId}/create-destination-emails`);
  return response.data;
};

// Step 5: Start Migration
export const startMigration = async (jobId, config) => {
  const response = await api.post('/start-migration', {
    jobId,
    config,
  });
  return response.data;
};

export const configureMigration = async (jobId, config) => {
  const response = await api.post('/configure', {
    jobId,
    config,
  });
  return response.data;
};

export const getProgress = async (jobId) => {
  const response = await api.get(`/progress/${jobId}`);
  return response.data;
};

export const getLogs = async (jobId) => {
  const response = await api.get(`/logs/${jobId}`);
  return response.data;
};

export const clearLogs = async (jobId) => {
  const response = await api.delete(`/logs/${jobId}`);
  return response.data;
};

export const getAllJobs = async () => {
  const response = await api.get('/jobs');
  return response.data;
};

export const updateJob = async (jobId, jobName, config) => {
  const response = await api.put(`/jobs/${jobId}`, {
    jobName,
    config,
  });
  return response.data;
};

export const deleteJob = async (jobId) => {
  const response = await api.delete(`/jobs/${jobId}`);
  return response.data;
};

// ========== SERVERS API ==========

export const getAllServers = async () => {
  const response = await serversApi.get('');
  return response.data;
};

export const getServer = async (id) => {
  const response = await serversApi.get(`/${id}`);
  return response.data;
};

export const searchServers = async (query) => {
  const response = await serversApi.get(`/search?q=${encodeURIComponent(query)}`);
  return response.data;
};

export const getServersByType = async (type) => {
  const response = await serversApi.get(`/type/${type}`);
  return response.data;
};

export const createServer = async (server) => {
  const response = await serversApi.post('', server);
  return response.data;
};

export const updateServer = async (id, server) => {
  console.log('API: updateServer called with id:', id);
  console.log('API: server payload:', JSON.stringify(server, null, 2));
  try {
    // Add cache-busting parameter
    const url = `/${id}?_t=${Date.now()}`;
    console.log('API: Making PUT request to:', url);
    const response = await serversApi.put(url, server);
    console.log('API: updateServer response status:', response.status);
    console.log('API: updateServer response data:', response.data);
    return response.data;
  } catch (error) {
    console.error('API: updateServer error:', error.message);
    console.error('API: error response status:', error.response?.status);
    console.error('API: error response data:', error.response?.data);
    throw error;
  }
};

export const deleteServer = async (id) => {
  const response = await serversApi.delete(`/${id}`);
  return response.data;
};

export const testServerConnection = async (id) => {
  const response = await serversApi.post(`/${id}/test-connection`);
  return response.data;
};

// ========== COMMANDS API ==========

const commandsApi = axios.create({
  baseURL: '/api/commands',
  headers: {
    'Content-Type': 'application/json',
  },
});
addAuthInterceptor(commandsApi);

export const executeCommand = async (serverId, command, timeoutMs = 60000) => {
  const response = await commandsApi.post('/execute', {
    serverId,
    command,
    timeoutMs,
  });
  return response.data;
};

export const getCommandSuggestions = async (serverId) => {
  const response = await commandsApi.get(`/suggestions/${serverId}`);
  return response.data;
};

// ========== WIZARD STATE API ==========

export const getWizardState = async (jobId) => {
  const response = await api.get(`/jobs/${jobId}/wizard-state`);
  return response.data;
};

export const updateWizardStep = async (jobId, step, completed = false) => {
  const response = await api.post(`/jobs/${jobId}/wizard-step`, {
    step,
    completed,
  });
  return response.data;
};

export const resetWizard = async (jobId) => {
  const response = await api.post(`/jobs/${jobId}/reset-wizard`);
  return response.data;
};

// ========== MIGRATION STREAMING API ==========

const MIGRATION_STREAM_API = '/api/migration-stream';

/**
 * Stream migration (imapsync) with Server-Sent Events
 * @param {string} jobId - The migration job ID
 * @param {Object} callbacks - Callback functions for streaming events
 * @returns {Object} - Controller with close() method
 */
export const streamMigration = (jobId, callbacks = {}, options = {}) => {
  const { onMessage, onError, onComplete } = callbacks;
  const params = new URLSearchParams();
  if (options.failedOnly) params.set('failedOnly', 'true');
  if (options.resume) params.set('resume', 'true');
  if (options.emails && options.emails.length) {
    params.set('emails', Array.isArray(options.emails) ? options.emails.join(',') : options.emails);
  }
  if (options.maxAge && Number(options.maxAge) > 0) {
    params.set('maxAge', String(Number(options.maxAge)));
  }
  const qs = params.toString();
  const url = `${MIGRATION_STREAM_API}/${jobId}${qs ? `?${qs}` : ''}`;

  const eventSource = new EventSource(withToken(url));
  let isClosed = false;
  
  // Safety timeout - force close after 10 minutes (migration can take long)
  const safetyTimeout = setTimeout(() => {
    if (!isClosed) {
      console.warn('Migration stream safety timeout reached - forcing close');
      isClosed = true;
      eventSource.close();
      onComplete?.();
    }
  }, 600000);
  
  eventSource.addEventListener('init', (e) => {
    onMessage?.({ type: 'init', data: e.data });
  });
  
  eventSource.addEventListener('progress', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'progress', data });
    } catch (err) {
      onMessage?.({ type: 'progress', data: e.data });
    }
  });
  
  eventSource.addEventListener('command', (e) => {
    onMessage?.({ type: 'command', data: e.data });
  });
  
  eventSource.addEventListener('output', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'output', data });
    } catch (err) {
      onMessage?.({ type: 'output', data: e.data });
    }
  });
  
  eventSource.addEventListener('error', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'error', data });
    } catch (err) {
      onMessage?.({ type: 'error', data: e.data });
    }
  });
  
  eventSource.addEventListener('success', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'success', data });
    } catch (err) {
      onMessage?.({ type: 'success', data: e.data });
    }
  });
  
  eventSource.addEventListener('failed', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'failed', data });
    } catch (err) {
      onMessage?.({ type: 'failed', data: e.data });
    }
  });
  
  eventSource.addEventListener('complete', (e) => {
    if (isClosed) return;
    isClosed = true;
    clearTimeout(safetyTimeout);
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'complete', data });
    } catch (err) {
      onMessage?.({ type: 'complete', data: e.data });
    }
    onComplete?.();
    eventSource.close();
  });
  
  eventSource.onopen = () => {
    console.log('SSE connection opened');
  };
  
  eventSource.onerror = (error) => {
    if (isClosed) return;
    isClosed = true;
    clearTimeout(safetyTimeout);
    console.error('SSE error:', error);
    // EventSource errors don't have message property - it's an Event object
    // The actual error usually comes through the 'error' event listener above
    // This handler is for connection failures (network issues, backend down, etc.)
    const enhancedError = {
      type: 'connection_error',
      message: 'Failed to connect to migration stream. Please check if the backend is running.',
      event: error
    };
    onError?.(enhancedError);
    eventSource.close();
  };
  
  return {
    close: () => {
      isClosed = true;
      clearTimeout(safetyTimeout);
      eventSource.close();
    }
  };
};

// ========== EMAIL CREATION STREAMING API ==========

const EMAIL_CREATION_API = '/api/email-creation';

/**
 * Create email accounts with Server-Sent Events (SSE) streaming
 * @param {string} jobId - The migration job ID
 * @param {string} serverId - The destination server ID
 * @param {Object} callbacks - Callback functions for streaming events
 * @returns {Object} - Controller with close() method
 */
export const streamEmailCreation = (jobId, serverId, callbacks = {}) => {
  const { onMessage, onError, onComplete } = callbacks;
  const url = `${EMAIL_CREATION_API}/stream/${jobId}?serverId=${serverId}`;
  
  const eventSource = new EventSource(withToken(url));
  let isClosed = false;
  
  // Safety timeout - force close after 2 minutes
  const safetyTimeout = setTimeout(() => {
    if (!isClosed) {
      console.warn('Email creation stream safety timeout reached - forcing close');
      isClosed = true;
      eventSource.close();
      onComplete?.();
    }
  }, 120000);
  
  eventSource.addEventListener('init', (e) => {
    onMessage?.({ type: 'init', data: e.data });
  });
  
  eventSource.addEventListener('progress', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'progress', data });
    } catch (err) {
      onMessage?.({ type: 'progress', data: e.data });
    }
  });
  
  eventSource.addEventListener('command', (e) => {
    onMessage?.({ type: 'command', data: e.data });
  });
  
  eventSource.addEventListener('output', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'output', data });
    } catch (err) {
      onMessage?.({ type: 'output', data: e.data });
    }
  });
  
  eventSource.addEventListener('error', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'error', data });
    } catch (err) {
      onMessage?.({ type: 'error', data: e.data });
    }
  });
  
  eventSource.addEventListener('success', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'success', data });
    } catch (err) {
      onMessage?.({ type: 'success', data: e.data });
    }
  });
  
  eventSource.addEventListener('failed', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'failed', data });
    } catch (err) {
      onMessage?.({ type: 'failed', data: e.data });
    }
  });
  
  eventSource.addEventListener('complete', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'complete', data });
    } catch (err) {
      onMessage?.({ type: 'complete', data: e.data });
    }
    onComplete?.();
    eventSource.close();
  });
  
  eventSource.onerror = (error) => {
    if (isClosed) return;
    isClosed = true;
    clearTimeout(safetyTimeout);
    
    // EventSource errors don't provide much detail, but we can check readyState
    const readyState = eventSource.readyState;
    const stateNames = { 0: 'CONNECTING', 1: 'OPEN', 2: 'CLOSED' };
    const stateName = stateNames[readyState] || 'UNKNOWN';
    
    console.error('SSE error - readyState:', stateName, 'url:', url, 'error:', error);
    
    // Create a more informative error object
    const enhancedError = new Error(
      readyState === 0 
        ? 'Failed to connect to server - check network/CORS'
        : readyState === 1
        ? 'Connection lost during stream - server may have closed connection'
        : 'Connection closed'
    );
    enhancedError.readyState = readyState;
    enhancedError.originalError = error;
    
    onError?.(enhancedError);
    eventSource.close();
  };
  
  return {
    close: () => {
      isClosed = true;
      clearTimeout(safetyTimeout);
      eventSource.close();
    }
  };
};

/**
 * Create a single email account with streaming output
 * @param {Object} request - Email creation request
 * @param {Object} callbacks - Callback functions
 */
export const streamSingleEmailCreation = (request, callbacks = {}) => {
  const { onMessage, onError, onComplete } = callbacks;
  
  fetch(`${EMAIL_CREATION_API}/create-single`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  }).then(async (response) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        onComplete?.();
        break;
      }
      
      const text = decoder.decode(value, { stream: true });
      // Parse SSE format
      const events = text.split('\n\n').filter(e => e.trim());
      
      events.forEach(event => {
        if (event.startsWith('data:')) {
          const data = event.substring(5).trim();
          try {
            const parsed = JSON.parse(data);
            onMessage?.({ type: parsed.type || 'message', data: parsed });
          } catch (e) {
            onMessage?.({ type: 'raw', data });
          }
        }
      });
    }
  }).catch(error => {
    onError?.(error);
  });
  
  return {
    close: () => { /* fetch cannot be aborted easily without AbortController */ }
  };
};

// ========== PASSWORD UPDATE STREAMING API ==========

const PASSWORD_UPDATE_API = '/api/password-update';

/**
 * Update passwords on destination server with Server-Sent Events (SSE) streaming
 * @param {string} jobId - The migration job ID
 * @param {Object} callbacks - Callback functions for streaming events
 * @returns {Object} - Controller with close() method
 */
export const streamPasswordUpdate = (jobId, callbacks = {}) => {
  const { onMessage, onError, onComplete } = callbacks;
  const url = `${PASSWORD_UPDATE_API}/stream/${jobId}`;
  
  const eventSource = new EventSource(withToken(url));
  
  eventSource.addEventListener('init', (e) => {
    onMessage?.({ type: 'init', data: e.data });
  });
  
  eventSource.addEventListener('progress', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'progress', data });
    } catch (err) {
      onMessage?.({ type: 'progress', data: e.data });
    }
  });
  
  eventSource.addEventListener('command', (e) => {
    onMessage?.({ type: 'command', data: e.data });
  });
  
  eventSource.addEventListener('output', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'output', data });
    } catch (err) {
      onMessage?.({ type: 'output', data: e.data });
    }
  });
  
  eventSource.addEventListener('error', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'error', data });
    } catch (err) {
      onMessage?.({ type: 'error', data: e.data });
    }
  });
  
  eventSource.addEventListener('success', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'success', data });
    } catch (err) {
      onMessage?.({ type: 'success', data: e.data });
    }
  });
  
  eventSource.addEventListener('failed', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'failed', data });
    } catch (err) {
      onMessage?.({ type: 'failed', data: e.data });
    }
  });
  
  eventSource.addEventListener('complete', (e) => {
    try {
      const data = JSON.parse(e.data);
      onMessage?.({ type: 'complete', data });
    } catch (err) {
      onMessage?.({ type: 'complete', data: e.data });
    }
    onComplete?.();
    eventSource.close();
  });
  
  eventSource.onopen = () => {
    console.log('Password update SSE connection opened');
  };
  
  eventSource.onerror = (error) => {
    console.error('Password update SSE error:', error);
    onError?.({
      type: 'connection_error',
      message: 'Failed to connect to password update stream',
      event: error
    });
    eventSource.close();
  };
  
  return {
    close: () => eventSource.close()
  };
};

// ========== EMAIL BATCHES API ==========

const EMAIL_BATCHES_BASE = '/api/email-batches';

// Create a separate axios instance for email batches (different base path)
const emailBatchesApi = axios.create({
  baseURL: EMAIL_BATCHES_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});
addAuthInterceptor(emailBatchesApi);

/**
 * Get all email batches
 */
export const getEmailBatches = async () => {
  const response = await emailBatchesApi.get('');
  return response.data;
};

/**
 * Get a specific email batch with all items
 */
export const getEmailBatch = async (id) => {
  const response = await emailBatchesApi.get(`/${id}`);
  return response.data;
};

/**
 * Create a new email batch
 */
export const createEmailBatch = async (name, description, items = []) => {
  const response = await emailBatchesApi.post('', {
    name,
    description,
    items
  });
  return response.data;
};

/**
 * Upload email batch from file
 */
export const uploadEmailBatch = async (file, name, description = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  if (description) formData.append('description', description);
  
  const response = await emailBatchesApi.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Update email batch (name, description)
 */
export const updateEmailBatch = async (id, updates) => {
  const response = await emailBatchesApi.put(`/${id}`, updates);
  return response.data;
};

/**
 * Delete an email batch
 */
export const deleteEmailBatch = async (id) => {
  await emailBatchesApi.delete(`/${id}`);
};

/**
 * Add item to email batch
 */
export const addEmailBatchItem = async (batchId, item) => {
  const response = await emailBatchesApi.post(`/${batchId}/items`, item);
  return response.data;
};

/**
 * Update item in email batch
 */
export const updateEmailBatchItem = async (batchId, itemId, updates) => {
  const response = await emailBatchesApi.put(`/${batchId}/items/${itemId}`, updates);
  return response.data;
};

/**
 * Delete item from email batch
 */
export const deleteEmailBatchItem = async (batchId, itemId) => {
  const response = await emailBatchesApi.delete(`/${batchId}/items/${itemId}`);
  return response.data;
};

/**
 * Toggle selection status for an item
 */
export const toggleEmailBatchItemSelection = async (batchId, itemId, selected) => {
  const response = await emailBatchesApi.put(`/${batchId}/items/${itemId}/select`, { selected });
  return response.data;
};

/**
 * Select or deselect all items in a batch
 */
export const selectAllEmailBatchItems = async (batchId, selected) => {
  const response = await emailBatchesApi.put(`/${batchId}/items/select-all`, { selected });
  return response.data;
};

// ── Web files migration (cPanel docroot → Plesk vhost) ──────────────────────
const WEB_FILES_API = '/api/web-files';

/**
 * Stream a web-files migration (rsync source cPanel → destination Plesk docroot).
 * @param {Object} params - sourceServerId, destServerId, sourcePath, destPath,
 *                          sourceUser?, chownUser?, group?, webPerms?, del?
 * @param {Object} callbacks - onMessage({type,data}), onError, onComplete
 * @returns {{close: Function}}
 */
export const streamWebFilesMigration = (params = {}, callbacks = {}) => {
  const { onMessage, onError, onComplete } = callbacks;
  const qs = new URLSearchParams();
  qs.set('sourceServerId', params.sourceServerId || '');
  qs.set('destServerId', params.destServerId || '');
  qs.set('sourcePath', params.sourcePath || '');
  qs.set('destPath', params.destPath || '');
  if (params.sourceUser) qs.set('sourceUser', params.sourceUser);
  if (params.sourceHost) qs.set('sourceHost', params.sourceHost);
  if (params.chownUser) qs.set('chownUser', params.chownUser);
  if (params.group) qs.set('group', params.group);
  if (params.webPerms) qs.set('webPerms', 'true');
  if (params.del) qs.set('delete', 'true');

  const eventSource = new EventSource(withToken(`${WEB_FILES_API}/migrate?${qs.toString()}`));
  let closed = false;
  const done = () => {
    if (closed) return;
    closed = true;
    eventSource.close();
    onComplete?.();
  };

  ['init', 'command'].forEach((name) =>
    eventSource.addEventListener(name, (e) => onMessage?.({ type: name, data: e.data }))
  );
  ['output', 'stderr'].forEach((name) =>
    eventSource.addEventListener(name, (e) => {
      let data;
      try { data = JSON.parse(e.data); } catch { data = { line: e.data }; }
      onMessage?.({ type: name, data });
    })
  );
  eventSource.addEventListener('complete', (e) => {
    let data;
    try { data = JSON.parse(e.data); } catch { data = {}; }
    onMessage?.({ type: 'complete', data });
    done();
  });
  eventSource.addEventListener('fatal', (e) => {
    onMessage?.({ type: 'fatal', data: e.data });
    done();
  });
  // Native connection error (not a server 'error' event — those are named 'stderr'/'fatal').
  eventSource.onerror = () => {
    if (!closed) { onError?.(new Error('Connection lost')); done(); }
  };

  return { close: done };
};

/**
 * Enqueue a durable web-files migration job. Returns { jobId, status }.
 */
export const enqueueWebFilesMigration = async (params = {}) => {
  const qs = new URLSearchParams();
  qs.set('sourceServerId', params.sourceServerId || '');
  qs.set('destServerId', params.destServerId || '');
  qs.set('sourcePath', params.sourcePath || '');
  qs.set('destPath', params.destPath || '');
  if (params.sourceUser) qs.set('sourceUser', params.sourceUser);
  if (params.sourceHost) qs.set('sourceHost', params.sourceHost);
  if (params.chownUser) qs.set('chownUser', params.chownUser);
  if (params.group) qs.set('group', params.group);
  if (params.webPerms) qs.set('webPerms', 'true');
  if (params.del) qs.set('delete', 'true');
  const res = await fetch(`${WEB_FILES_API}/enqueue?${qs.toString()}`, {
    method: 'POST',
    headers: authFetchHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Enqueue failed (${res.status})`);
  }
  return res.json();
};

/**
 * Tail a durable web-files job's live output over SSE.
 * @returns {{close: Function}}
 */
export const streamWebFilesJob = (jobId, callbacks = {}) => {
  const { onMessage, onError, onComplete } = callbacks;
  const eventSource = new EventSource(withToken(`${WEB_FILES_API}/jobs/${jobId}/stream`));
  let closed = false;
  const done = () => {
    if (closed) return;
    closed = true;
    eventSource.close();
    onComplete?.();
  };
  eventSource.addEventListener('init', (e) => onMessage?.({ type: 'init', data: e.data }));
  ['output', 'stderr'].forEach((name) =>
    eventSource.addEventListener(name, (e) => {
      let data;
      try { data = JSON.parse(e.data); } catch { data = { line: e.data }; }
      onMessage?.({ type: name, data });
    })
  );
  eventSource.addEventListener('complete', (e) => {
    let data;
    try { data = JSON.parse(e.data); } catch { data = {}; }
    onMessage?.({ type: 'complete', data });
    done();
  });
  eventSource.addEventListener('fatal', (e) => {
    onMessage?.({ type: 'fatal', data: e.data });
    done();
  });
  eventSource.addEventListener('ping', () => {}); // keepalive
  eventSource.onerror = () => {
    if (!closed) { onError?.(new Error('Connection lost')); done(); }
  };
  return { close: done };
};

export default api;
